import { useRequestPromise } from 'hooks/useRequest';
import useDeclarationsFilters from './useDeclarationsFilters';
import { exportDeclarationsCSV, listDeclarations } from 'store/declarations/client';
import { useCallback, useMemo, useState } from 'react';
import { getTableChangeParams, SortDirection } from 'utils/tableHelpers';
import { Declaration, GetDeclarationsSortParameter } from 'store/declarations/declaration';
import { ModalFilters } from '../DeclarationFiltersModal';
import { TableRowSelection } from 'antd/lib/table/interface';
import { triggerBrowserDownload } from 'utils/download-utils';
import { getErrorMessage } from 'utils/getErrorMessage';
import { DEFAULT_INTERNAL_ERROR_MESSAGE } from 'core/utils/constants';
import Notification from 'components/ui/base/notification/Notification';
import { Ics2TableRow, isIcs2GroupRow } from 'store/declarations/eu/ics2/ics2-declaration-list';

type AdditionalParams = Record<string, unknown>;

type Options = {
    listFn?: typeof listDeclarations;
};

const useDeclarationsTable = (jobId?: string, additionalParams?: AdditionalParams, options?: Options) => {
    const {
        queryInputVal,
        appliedParams,
        dates,
        archived,
        tags,
        clearFilter: clearFilterInternal,
        clearAll: clearAllInternal,
        setDates: setDatesInternal,
        setPage,
        setQueryInputVal: setQueryInputValInternal,
        setSortState,
        setSize,
        setStatuses,
        setCountry,
        setMessageTypes,
        setDeclarationTypes,
        setArchived: setArchivedInternal,
        setIsCoreTemplate: setIsCoreTemplateInternal,
    } = useDeclarationsFilters();

    const [selectedIds, setSelectedIds] = useState<React.Key[]>([]);
    const [isExportingCSV, setIsExportingCSV] = useState(false);

    const resetSelection = useCallback(() => setSelectedIds([]), []);

    const rowSelection: TableRowSelection<Declaration> = useMemo(
        () => ({
            type: 'checkbox',
            selectedRowKeys: selectedIds,
            onChange: (keys) => setSelectedIds(keys),
            // An ICS2 group row summarises the declarations under it rather than being one of them,
            // and the bulk actions act on single declarations — so only its rows can be picked. The
            // disabled flag is what keeps the group out of the header's select-all.
            getCheckboxProps: (record) => ({
                disabled: isIcs2GroupRow(record as unknown as Ics2TableRow),
            }),
            renderCell: (_value, record, _index, originNode) =>
                isIcs2GroupRow(record as unknown as Ics2TableRow) ? null : originNode,
        }),
        [selectedIds]
    );

    const handleExportDeclarationsCSV = useCallback(async () => {
        if (selectedIds.length < 1) return;

        setIsExportingCSV(true);
        try {
            const blob = await exportDeclarationsCSV(selectedIds.map(String));
            triggerBrowserDownload(blob, 'declarations.csv');
            Notification({
                type: 'success',
                messageTitle: 'Export Successful',
                description: 'Declarations CSV exported successfully.',
            });
        } catch (error) {
            const errorMessage = getErrorMessage(error) || DEFAULT_INTERNAL_ERROR_MESSAGE;
            Notification({
                type: 'error',
                messageTitle: 'Export Failed',
                description: errorMessage,
            });
        } finally {
            setIsExportingCSV(false);
        }
    }, [selectedIds]);

    const setQueryInputVal = useCallback(
        (val: string) => {
            resetSelection();
            setQueryInputValInternal(val);
        },
        [resetSelection, setQueryInputValInternal]
    );

    const setDates = useCallback(
        (range: Parameters<typeof setDatesInternal>[0]) => {
            resetSelection();
            setDatesInternal(range);
        },
        [resetSelection, setDatesInternal]
    );

    const setArchived = useCallback(
        (val: Parameters<typeof setArchivedInternal>[0]) => {
            resetSelection();
            setArchivedInternal(val);
        },
        [resetSelection, setArchivedInternal]
    );

    const setIsCoreTemplate = useCallback(
        (val: Parameters<typeof setIsCoreTemplateInternal>[0]) => {
            resetSelection();
            setIsCoreTemplateInternal(val);
        },
        [resetSelection, setIsCoreTemplateInternal]
    );

    const clearFilter = useCallback(
        (key: string, value?: string) => {
            resetSelection();
            clearFilterInternal(key, value);
        },
        [resetSelection, clearFilterInternal]
    );

    const clearAll = useCallback(() => {
        resetSelection();
        clearAllInternal();
    }, [resetSelection, clearAllInternal]);

    const {
        data,
        isLoading: isLoadingDeclarations,
        refetch: _refetchDeclarations,
    } = useRequestPromise(
        options?.listFn ?? listDeclarations,
        { withSignal: true, args: [{ ...appliedParams, ...additionalParams, jobId }] },
        [appliedParams]
    );

    const refetchDeclarations = useCallback(
        (params: any) => _refetchDeclarations({ ...params, ...additionalParams, jobId }),
        [_refetchDeclarations, additionalParams, jobId]
    );

    const handleTableChange = useCallback(
        (antPagination: any, _filters: any, sorter: any) => {
            const raw = getTableChangeParams({ pagination: antPagination, sorter });

            const { page, size, sortParameter, sortDirection } = (raw || {}) as {
                page?: number;
                size?: number;
                sortParameter?: string;
                sortDirection?: SortDirection;
            };
            setPage((p) => (typeof page === 'number' ? page : p));
            setSize((s) => (typeof size === 'number' ? size : s));

            // Clear sorting from URL params
            if (!sortParameter || !sortDirection) {
                setSortState({ sort: undefined, sortDirection: undefined });
            } else {
                setSortState({
                    sort: sortParameter as GetDeclarationsSortParameter,
                    sortDirection,
                });
            }
        },
        [setPage, setSize, setSortState]
    );

    const handleFiltersChange = useCallback(
        (patch: Partial<ModalFilters>) => {
            if (
                patch.query !== undefined ||
                patch.declarationExternalEntity !== undefined ||
                patch.declarationStatus !== undefined ||
                patch.declarationType !== undefined ||
                patch.declarationInternalType !== undefined
            ) {
                resetSelection();
            }

            if (patch.query !== undefined) {
                setQueryInputValInternal(patch.query);
                setPage(0);
            }

            if (patch.declarationExternalEntity !== undefined) {
                setCountry(patch.declarationExternalEntity);
                setPage(0);
            }

            if (patch.declarationStatus !== undefined) {
                setStatuses(patch.declarationStatus);
                setPage(0);
            }

            if (patch.declarationType !== undefined) {
                setMessageTypes(patch.declarationType);
                setPage(0);
            }

            if (patch.declarationInternalType !== undefined) {
                setDeclarationTypes(patch.declarationInternalType);
                setPage(0);
            }
        },
        [
            resetSelection,
            setQueryInputValInternal,
            setPage,
            setCountry,
            setStatuses,
            setMessageTypes,
            setDeclarationTypes,
        ]
    );

    return {
        queryInputVal,
        appliedParams,
        tags,
        dates,
        setPage,
        setSize,
        setSortState,
        archived,
        clearFilter,
        clearAll,
        setQueryInputVal,
        setStatuses,
        setCountry,
        setMessageTypes,
        setDeclarationTypes,
        setDates,
        setArchived,
        setIsCoreTemplate,

        data,
        isLoadingDeclarations,
        refetchDeclarations,
        handleTableChange,
        handleFiltersChange,

        selectedIds,
        rowSelection,
        resetSelection,
        handleExportDeclarationsCSV,
        isExportingCSV,
    };
};

export default useDeclarationsTable;
